import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";


const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};


const IpdVitalsReadings = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  
  // Master Dropdowns
  const [vitalTypes, setVitalTypes] = useState([]);
  
  // Search States
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [admissionResults, setAdmissionResults] = useState([]);
  const [showAdmissionDropdown, setShowAdmissionDropdown] = useState(false);
  const [currentMode, setCurrentMode] = useState("add");

  const [staffSearch, setStaffSearch] = useState("");
  const [staffResults, setStaffResults] = useState([]);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [formData, setFormData] = useState({
    admission_id: "",
    patient_id: "",
    patient_name: "",
    department: "",
    ward_type: "",
    room_no: "",
    bed_no: "",
    age: "",
    gender: "",
    vitals_array: []
  });

  const [currentVital, setCurrentVital] = useState({
    reading_time: new Date().toISOString().slice(0, 16),
    vital_type_id: "",
    vital_type_name: "",
    reading_value: "",
    remarks: ""
  });

  // --- MASTER DATA ---
  useEffect(() => {
    const fetchMasters = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      try {
        const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/vital-types?employee_id=${user?.id}`);
        if (res.ok) {
            const data = await res.json();
            setVitalTypes(data.filter(v => v.status === "Active"));
        }
      } catch (err) {
        console.error("Error fetching vital types");
      }
    };
    fetchMasters();
  }, []);

  // --- ADMISSION SEARCH ---
  const handleAdmissionSearch = async (val) => {
    setAdmissionSearch(val);
    if (val.length < 3) {
      setAdmissionResults([]);
      setShowAdmissionDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/admission-vitals/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setAdmissionResults(data);
      setShowAdmissionDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const loadExistingVitals = async (admissionId) => {
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd-vitals/${admissionId}`);
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, vitals_array: data }));
      }
    } catch (err) {
      console.error("Failed to load existing vitals");
    }
  };

  const selectAdmission = (adm) => {
    setFormData(prev => ({
      ...prev,
      admission_id: adm.admission_id,
      patient_id: adm.patient_id,
      patient_name: adm.patient_name,
      department: adm.department,
      ward_type: adm.ward_type,
      room_no: adm.room_no,
      bed_no: adm.bed_no,
      age: adm.age,
      gender: adm.gender
    }));
    setAdmissionSearch(adm.admission_id);
    setShowAdmissionDropdown(false);
    
    if (screenAction === "edit" || screenAction === "view") {
        loadExistingVitals(adm.admission_id);
    }
    
    if (screenAction === "edit" || screenAction === "add") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- STAFF SEARCH ---
  const handleStaffSearch = async (val) => {
    setStaffSearch(val);
    if (val.length < 3) {
      setStaffResults([]);
      setShowStaffDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/staff/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setStaffResults(data);
      setShowStaffDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const selectStaff = (staff) => {
    setSelectedStaff(staff);
    setStaffSearch(staff.staff_name);
    setShowStaffDropdown(false);
  };

  // --- VITAL ARRAY MANAGEMENT ---
  const addVital = () => {
    if (!selectedStaff) return alert("Please search and select the Staff Member taking the vitals.");
    if (!currentVital.vital_type_id || !currentVital.reading_value) return alert("Please enter the Vital Type and Reading Value.");

    setFormData(prev => ({
      ...prev,
      vitals_array: [{
          ...currentVital,
          staff_cd: selectedStaff.staff_cd,
          staff_name: selectedStaff.staff_name,
          role: selectedStaff.role
      }, ...prev.vitals_array] // Unshift to put newest at the top
    }));

    setCurrentVital({
      reading_time: new Date().toISOString().slice(0, 16),
      vital_type_id: "",
      vital_type_name: "",
      reading_value: "",
      remarks: ""
    });
  };

  const removeVital = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      vitals_array: prev.vitals_array.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // --- ACTIONS ---
  const clearForm = () => {
    setFormData({ admission_id: "", patient_id: "", patient_name: "", department: "", ward_type: "", room_no: "", bed_no: "", age: "", gender: "", vitals_array: [] });
    setAdmissionSearch("");
    setStaffSearch("");
    setSelectedStaff(null);
    setIsDisabled(screenAction === "view");
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);


useEffect(() => {
    if (screenAction === "add") { 
      setCurrentMode("add"); 
      clearForm(); 
      setIsDisabled(false); 
    }
    if (screenAction === "edit") { 
      setCurrentMode("edit"); 
      setIsDisabled(false); 
    }
    if (screenAction === "view") { 
      setCurrentMode("view"); 
      setIsDisabled(true); 
    }
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);


  const handleSubmit = async () => {
    if (!formData.admission_id) return alert("Please select an Admission.");
    if (formData.vitals_array.length === 0) return alert("No vitals to save.");

    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      admission_id: formData.admission_id,
      created_by: user.id,
      vitals: formData.vitals_array
    };

    // 🔥 FIX: Check 'currentMode' instead of 'screenAction'
    const method = currentMode === "edit" ? "PUT" : "POST";
    const url = currentMode === "edit"
      ? `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-vitals/${formData.admission_id}`
      : `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-vitals`;

    try {
      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert(currentMode === "edit" ? "Vitals Updated Successfully!" : "Vitals Logged Successfully!");
        if (currentMode === "add") clearForm();
      } else {
        alert("Error saving vitals.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  const printForm = () => {
    if (!formData.admission_id) return alert("Please select an admission to print.");
    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Clinical Vitals - ${formData.admission_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .data-item { font-size: 13px; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; color: #555; display: inline-block; width: 120px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f4f0fa; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Inpatient Clinical Monitoring</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin:0; color:#282f81;">VITALS RECORD</h2>
              <p style="margin:2px 0; font-size:12px;">Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Patient & Admission Details</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Admission ID:</span> ${formData.admission_id}</div>
            <div class="data-item"><span class="label">Patient Name:</span> ${formData.patient_name}</div>
            <div class="data-item"><span class="label">Patient ID:</span> ${formData.patient_id}</div>
            <div class="data-item"><span class="label">Age / Gender:</span> ${formData.age} / ${formData.gender}</div>
            <div class="data-item" style="grid-column: span 2"><span class="label">Location:</span> ${formData.department} | ${formData.ward_type} (Room: ${formData.room_no} / Bed: ${formData.bed_no})</div>
          </div>

          <div class="section-header">Recorded Vitals</div>
          <table>
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Vital Parameter</th>
                <th>Reading</th>
                <th>Recorded By (Staff)</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              ${formData.vitals_array.map(v => `
                <tr>
                  <td>${new Date(v.reading_time).toLocaleString()}</td>
                  <td style="font-weight:bold;">${v.vital_type_name}</td>
                  <td>${v.reading_value}</td>
                  <td>${v.staff_name} (${v.role})</td>
                  <td>${v.remarks || '-'}</td>
                </tr>
              `).join('')}
              ${formData.vitals_array.length === 0 ? '<tr><td colspan="5" style="text-align:center;">No vitals recorded yet.</td></tr>' : ''}
            </tbody>
          </table>

          <div class="footer">System Generated Report - Emedha HMS</div>
        </body>
      </html>
    `;
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

return (
    <div className="registration-container fade-in">
      
      {/* 🔥 FIX: TOP SEARCH BOX (Only visible in Edit or View Mode) */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Admission </label>
          <input
            type="text"
            value={admissionSearch}
            placeholder="Search by Admission ID, Patient ID, Name, or Mobile..."
            onChange={(e) => handleAdmissionSearch(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          {showAdmissionDropdown && admissionResults.length > 0 && (
            <div className="patient-dropdown">
              {admissionResults.map((adm) => (
                <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                  <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id} | Ph: {adm.mobile}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* 1. PATIENT & ADMISSION DETAILS */}
        <div className="form-section-header"><h3>1. Patient & Admission Details</h3></div>
        
        <div 
          className="form-grid" 
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px", 
            alignItems: "flex-end" 
          }}
        >
          {/* Show inline search ONLY in "add" mode. Show read-only ID in "edit/view" mode. */}
          {screenAction === "add" ? (
            <div className="input-box" style={{ position: "relative" }}>
              <label>Search Admission <span style={{color:'red'}}>*</span></label>
              <input 
                type="text" 
                value={admissionSearch} 
                onChange={(e) => handleAdmissionSearch(e.target.value)} 
                placeholder="Start typing to search..." 
                autoComplete="off"
              />
              {showAdmissionDropdown && admissionResults.length > 0 && (
                <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                  {admissionResults.map((adm) => (
                    <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                      <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="input-box read-only">
              <label>Admission ID</label>
              <input type="text" value={formData.admission_id} readOnly placeholder="Select from search above" />
            </div>
          )}

          <div className="input-box read-only">
            <label>Patient Name</label>
            <input type="text" value={formData.patient_name} readOnly />
          </div>
          
          <div className="input-box read-only">
            <label>Age / Gender</label>
            <input type="text" value={formData.age && formData.gender ? `${formData.age} / ${formData.gender}` : ""} readOnly />
          </div>
          
          <div className="input-box read-only">
            <label>Location</label>
            <input type="text" value={formData.department ? `${formData.department} - ${formData.ward_type} (Bed: ${formData.bed_no})` : ""} readOnly />
          </div>
        </div>

        {/* 2. STAFF SELECTION */}
        <div className="form-section-header" style={{ marginTop: "20px" }}><h3>2. Staff Details</h3></div>
        <div className="form-grid">
          <div className="input-box" style={{ position: "relative", maxWidth: "400px", width: "100%" }}>
            <label>Search Staff (Name, ID, Mobile) <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={staffSearch} 
              onChange={(e) => handleStaffSearch(e.target.value)} 
              placeholder="Search Staff Member..." 
              disabled={isDisabled}
              autoComplete="off"
            />
            {showStaffDropdown && staffResults.length > 0 && (
              <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                {staffResults.map((stf) => (
                  <div key={stf.staff_cd} className="patient-item" onClick={() => selectStaff(stf)}>
                    {stf.staff_cd} | {stf.staff_name} | {stf.role}
                  </div>
                ))}
              </div>
            )}
            {selectedStaff && (
               <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: theme.primaryBlue, fontWeight: "bold" }}>
                 ✓ Selected: {selectedStaff.staff_name} ({selectedStaff.role}) - {selectedStaff.department}
               </p>
            )}
          </div>
        </div>

        {/* 3. ADD VITALS */}
        <div className="form-section-header" style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>3. Log Vitals</h3>
          <button 
            type="button" 
            onClick={addVital} 
            disabled={isDisabled || !formData.admission_id}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: theme.primaryBlue, 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: isDisabled || !formData.admission_id ? 'not-allowed' : 'pointer', 
              fontWeight: 'bold',
              opacity: isDisabled || !formData.admission_id ? 0.6 : 1
            }}>
            + Add Vital
          </button>
        </div>

        <div 
          className="form-grid" 
          style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "15px", 
            borderRadius: "8px", 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px",
            alignItems: "flex-start" 
          }}
        >
          <div className="input-box">
            <label>Date & Time</label>
            <input 
              type="datetime-local" 
              value={currentVital.reading_time} 
              onChange={(e) => setCurrentVital({...currentVital, reading_time: e.target.value})} 
              disabled={isDisabled} 
            />
          </div>

          <div className="input-box">
            <label>Vital Type</label>
            <select 
              value={currentVital.vital_type_id} 
              disabled={isDisabled}
              onChange={(e) => {
                const vt = vitalTypes.find(v => v.vital_type_id.toString() === e.target.value);
                setCurrentVital({ ...currentVital, vital_type_id: e.target.value, vital_type_name: vt ? vt.vital_type_name : "" });
              }}
            >
              <option value="">Select Vital</option>
              {vitalTypes.map(v => <option key={v.vital_type_id} value={v.vital_type_id}>{v.vital_type_name} ({v.unit})</option>)}
            </select>
          </div>

          <div className="input-box">
            <label>Reading</label>
            <input 
              type="text" 
              value={currentVital.reading_value} 
              onChange={(e) => setCurrentVital({...currentVital, reading_value: e.target.value})} 
              disabled={isDisabled} 
              placeholder="e.g. 98.6" 
            />
          </div>

          <div className="input-box">
            <label>Remarks</label>
            <input 
              type="text" 
              value={currentVital.remarks} 
              onChange={(e) => setCurrentVital({...currentVital, remarks: e.target.value})} 
              disabled={isDisabled} 
              placeholder="Optional notes..." 
            />
          </div>
        </div>

        {/* 4. VITALS LIST TABLE */}
        {formData.vitals_array.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: theme.primaryDark }}>Recorded Vitals ({formData.vitals_array.length})</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', minWidth: '600px' }}>
                <thead style={{ backgroundColor: theme.bgLight, borderBottom: '2px solid #ccc' }}>
                  <tr>
                    <th style={{ padding: '10px' }}>Time</th>
                    <th style={{ padding: '10px' }}>Staff</th>
                    <th style={{ padding: '10px' }}>Vital Type</th>
                    <th style={{ padding: '10px' }}>Reading</th>
                    <th style={{ padding: '10px' }}>Remarks</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
               <tbody>
                  {formData.vitals_array.map((vital, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd', backgroundColor: vital.id ? '#ffffff' : '#f4fcff' }}>
                      
                      {/* Edit Time */}
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="datetime-local" 
                          value={new Date(vital.reading_time).toISOString().slice(0, 16)} 
                          onChange={(e) => {
                            const newArr = [...formData.vitals_array];
                            newArr[index].reading_time = e.target.value;
                            setFormData({ ...formData, vitals_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent' }}
                        />
                      </td>
                      
                      {/* Staff (Read-Only to prevent heavy nested dropdowns) */}
                      <td style={{ padding: '10px' }}>
                        {vital.staff_name} <br/>
                        <span style={{fontSize: '10px', color: '#888'}}>({vital.staff_cd})</span>
                      </td>

                      {/* Edit Vital Type directly in the row! */}
                      <td style={{ padding: '10px' }}>
                        <select 
                          value={vital.vital_type_id} 
                          disabled={isDisabled}
                          onChange={(e) => {
                            const vt = vitalTypes.find(v => v.vital_type_id.toString() === e.target.value);
                            const newArr = [...formData.vitals_array];
                            newArr[index].vital_type_id = e.target.value;
                            newArr[index].vital_type_name = vt ? vt.vital_type_name : "";
                            setFormData({ ...formData, vitals_array: newArr });
                          }}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', fontWeight: 'bold', color: theme.primaryBlue }}
                        >
                          <option value="">Select Vital</option>
                          {vitalTypes.map(v => <option key={v.vital_type_id} value={v.vital_type_id}>{v.vital_type_name}</option>)}
                        </select>
                      </td>
                      
                      {/* Edit Reading Value */}
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="text" 
                          value={vital.reading_value} 
                          onChange={(e) => {
                            const newArr = [...formData.vitals_array];
                            newArr[index].reading_value = e.target.value;
                            setFormData({ ...formData, vitals_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '80px', fontWeight: 'bold' }}
                        />
                      </td>
                      
                      {/* Edit Remarks */}
                      <td style={{ padding: '10px' }}>
                         <input 
                          type="text" 
                          value={vital.remarks || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.vitals_array];
                            newArr[index].remarks = e.target.value;
                            setFormData({ ...formData, vitals_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%' }}
                        />
                      </td>
                      
                      {/* Delete Button (Unlocked for existing records too!) */}
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {!isDisabled && ( 
                          <button 
                            type="button" 
                            onClick={() => removeVital(index)} 
                            style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', padding: '5px' }}
                            title="Remove Record"
                          >
                            X
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>
    </div>
  );

};

export default IpdVitalsReadings;
